/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 *
 * This  software  has  been  provided pursuant  to  a  License
 * Agreement  containing  restrictions on  its  use.   The  software
 * contains  valuable  trade secrets and proprietary information  of
 * Pegasystems Inc and is protected by  federal   copyright law.  It
 * may  not be copied,  modified,  translated or distributed in  any
 * form or medium,  disclosed to third parties or used in any manner
 * not provided for in  said  License Agreement except with  written
 * authorization from Pegasystems Inc.
 */

import type { Draft } from '@reduxjs/toolkit';

import Utils from '../../../utils/utils';
import constants from '../../../constants';
import DataPageUtils from '../../../datapage/index';
import { CONTAINER_NAMES } from '../../../container/container-utils';

/**
 * This function helps to add the shared data
 * @param draft - immer draft
 * @param sharedData - shared data
 * @private
 */
const addSharedData = (draft: Draft<Record<string, unknown>>, sharedData: { [key: string]: any }) => {
  DataPageUtils.updateDataPageStore(sharedData);
  Object.keys(sharedData).forEach((key) => {
    if (Utils.getIn(draft, [constants.SHARED, key])) {
      Object.keys(sharedData[key]).forEach((nestedKey) => {
        Utils.setIn(draft, [constants.SHARED, key, nestedKey], sharedData[key][nestedKey]);
      });
    } else {
      Utils.setIn(draft, [constants.SHARED, key], sharedData[key]);
    }
  });
};

/**
 * This function helps to add the shared data
 * @param draft - immer draft
 * @param payload - payload info e.g. { data: { shared: {} }, context: 'app/primary_1' }
 * @private
 */
export const processSharedData = (draft: Draft<Record<string, unknown>>, payload: any) => {
  const { data: { shared = {} } = {}, context } = payload;
  addSharedData(draft, shared);

  if (context) {
    Utils.deleteIn(draft, [context, 'shared']);
  }
};

export const setCaseViewMode = (draft: Draft<Record<string, unknown>>, payload: any, skipDefaultValue = false) => {
  const { context, caseViewMode = !skipDefaultValue ? constants.PERFORM_MODE : undefined, containerName } = payload;
  if (containerName !== CONTAINER_NAMES.WORKAREA && caseViewMode) {
    Utils.setIn(draft, [context, 'context_data', 'caseViewMode'], caseViewMode);
  }
};

export const setActionTypeAndAssignmentId = (draft: Draft<Record<string, unknown>>, payload: any) => {
  const { context } = payload;
  Utils.setIn(draft, [context, 'context_data', 'actionType'], payload.actionType);
  Utils.setIn(draft, [context, 'context_data', 'assignmentID'], payload.assignmentID);
};

export const deleteBlockingActionCounter = (draft: Draft<Record<string, unknown>>, payload: { context: string }) => {
  const { context } = payload;
  Utils.deleteIn(draft, [context, 'context_data', 'blocking_action_counter']);
  Utils.deleteIn(draft, [context, 'context_data', 'deferred_action_queue']);
};
